<template>
  <VeeForm v-slot="{ errors, handleSubmit }" as="">
    <div class="flex flex-col justify-between rounded-md w-full">
      <form
        autocomplete="off"
        class="w-80 overflow-auto"
        @submit="handleSubmit($event, onSubmit)"
      >
        <div class="bg-neutral-secondary p-4 rounded-md">
          <VeeField
            v-slot="{ field }"
            v-model="formModel.isMaxQuantity"
            name="isMaxQuantity"
            rules="required"
          >
            <NRadioGroup
              v-model="formModel.isMaxQuantity"
              v-bind="field"
              :show-error="true"
              :required-sign="false"
              required
            >
              <NBox class="rounded-md">
                <NRadio
                  :value="false"
                  class="w-full p-3 border-b border-neutral-secondary"
                >
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-primary"
                  >
                    {{ t('common.optionalCount') }}
                  </NText>
                </NRadio>

                <NRadio :value="true" class="w-full p-3">
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-primary"
                  >
                    {{ t('option.maximumCashCount') }}
                  </NText>
                </NRadio>
              </NBox>
            </NRadioGroup>
          </VeeField>

          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary block pr-3 my-3 mb-2"
          >
            {{ t('option.cashCount') }}
          </NText>
          <VeeField
            v-slot="{ field }"
            v-model="formModel.quantity"
            name="quantity"
            :rules="
              !formModel.isMaxQuantity && {
                required: [t('option.cashCount')],
                min: [1, t('option.cashCount'), '', 0],
              }
            "
          >
            <NInput
              v-model="formModel.quantity"
              v-mask="{
                alias: 'positiveNumber',
              }"
              input-class="text-right"
              v-bind="field"
              :error="errors.quantity"
              :show-error="true"
              :required-sign="false"
              type="tel"
              dir="ltr"
              :min="1"
              :required="!formModel.isMaxQuantity"
              :disabled="formModel.isMaxQuantity"
              @focusin="$event.target.select()"
            >
              <template #postfix></template>
            </NInput>
          </VeeField>

          <div class="flex justify-between items-center mt-3">
            <NText
              variant="overline"
              weight="medium"
              class="text-brand-primary block"
            >
              {{ t('option.openPositionsCount') }}
            </NText>

            <NText
              variant="overline"
              weight="medium"
              class="text-brand-primary block"
            >
              {{ openPositionQuantity }}
            </NText>
          </div>

          <hr class="my-4 border-neutral-tertiary" />

          <VeeField v-slot="{ field }" name="shareInProportion">
            <div class="flex items-center mb-3">
              <label class="flex items-center">
                <NCheckbox v-model="formModel.shareInProportion" v-bind="field">
                  <NText
                    class="text-neutral-primary"
                    weight="medium"
                    variant="caption1"
                  >
                    {{ t('option.agreeToSettleByProportion') }}
                  </NText>
                  <br />
                  <NText class="text-neutral-secondary" variant="caption2">
                    {{ t('option.agreeToSettleByProportionDescription') }}
                  </NText>
                </NCheckbox>
              </label>
            </div>
          </VeeField>
        </div>

        <div class="flex gap-2 justify-center items-center mt-3">
          <NButton
            v-gid="{
              module: 'option',
              screen: 'home',
              section: 'optionTbl',
              component: isEditMode
                ? 'optionBuySettelmentEdit'
                : 'optionBuySettelmentSubmit',
              disabled: isSubmitBtnDisable,
            }"
            variant="fill"
            class="font-bold w-full"
            type="submit"
            size="small"
            :disabled="isSubmitBtnDisable"
            :loading="loading"
          >
            {{ isEditMode ? t('common.submitEdit') : t('misc.submit') }}
          </NButton>

          <NButton
            variant="outline"
            class="font-bold w-full"
            color="red"
            size="small"
            @click="remove()"
          >
            {{ t('common.cancel') }}
          </NButton>
        </div>
      </form>
    </div>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { NText, useDialog, useToast } from '@negar/core';
import {
  OPTION_QUERY_KEYS,
  useCreateCashSettlementQuery,
  useUpdateCashSettlementQuery,
} from '@core/option';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import vMask from '@/directives/mask';

type TBuyCashSettlementProps = {
  isin: string;
  isEditMode: boolean;
  openPositionQuantity: number;
  model?: {
    quantity: number;
    isMaxQuantity: boolean;
    shareInProportion: boolean;
  };
};

const { t } = useI18n();
const toast = useToast();
const { remove } = useDialog();
const queryClient = useQueryClient();

const props = defineProps<TBuyCashSettlementProps>();

const formModel = reactive({
  shareInProportion: Boolean(props.model?.shareInProportion),
  isMaxQuantity: Boolean(props.model?.isMaxQuantity),
  quantity: props.model?.quantity ? props.model.quantity : null,
});

const { createCashSettlement, isLoadingCreateCashSettlement } =
  useCreateCashSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.submit', {
          settlementType: t('option.settlementRequest.cash'),
        }),
        duration: 3000,
      });

      remove();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    onReject: (error) => {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const { updateCashSettlement, isLoadingUpdateCashSettlement } =
  useUpdateCashSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.update', {
          settlementType: t('option.settlementRequest.cash'),
        }),
        duration: 3000,
      });

      remove();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    onReject: (error) => {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const isSubmitBtnDisable = computed(
  () => !formModel.quantity || formModel.quantity <= 0 || loading.value
);

const loading = computed(
  () =>
    isLoadingUpdateCashSettlement.value || isLoadingCreateCashSettlement.value
);

const onSubmit = () => {
  if (props.isEditMode) {
    return updateCashSettlement({
      formModel: { ...formModel, isin: props.isin },
    });
  }

  return createCashSettlement({
    formModel: { ...formModel, isin: props.isin },
  });
};

const handleChangeIsMaxQuantity = () => {
  if (formModel.isMaxQuantity) {
    formModel.quantity = props.openPositionQuantity;
  }
};

watch(() => formModel.isMaxQuantity, handleChangeIsMaxQuantity, {
  immediate: true,
});
</script>
